<script setup lang="ts">
import type { BaseItem } from '~/models/Content/BaseItem'
import type { Task } from '~/models/AssignTask'
import { useI18n } from 'vue-i18n'
import { faArrowRightLong } from '@fortawesome/duotone-regular-svg-icons'
import { PendoFeature } from '~/models/Pendo'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useDescriptor from '~/composables/useDescriptor'
import { isExamTask } from '~/composables/useAssignTags'
import LinkOrAnchor from '~/components/utils/LinkOrAnchor.vue'
import SharedCardBadge from '~/components/cards/badges/SharedCardBadge.vue'

const { tasks } = defineProps<{ tasks: (Task & { content?: BaseItem })[] }>()

const { t } = useI18n()
const { buildResourceUrl, buildAssignedTaskUrl } = useUrlBuilder()
const { getDescriptor } = useDescriptor()
</script>

<template>
  <div v-if="tasks.length" class="bg-seagreen-5 text-seagreen-50 *:divide-seagreen-30 *:border-seagreen-30 flex h-fit flex-col rounded-xl shadow">
    <div class="flex flex-col space-y-3 pt-3">
      <span class="pl-3 text-lg font-semibold" v-text="t('share.messages.recent')" />
      <hr aria-hidden="true">
    </div>
    <ul class="divide-y border-b">
      <li
        v-for="task in tasks"
        :key="`task-${task.taskId}`"
      >
        <LinkOrAnchor
          v-if="isExamTask(task)"
          :link="buildAssignedTaskUrl(task)"
          :data-pendo="PendoFeature.TermExam.GoToTask"
          class="hover:bg-seagreen-pop flex flex-col px-5 py-3 transition focus-visible:ring"
        >
          <div class="relative">
            <SharedCardBadge v-if="task?.homework" type="banner" />
            <span class="text-sm" v-text="t('termExam.title')" />
            <p class="font-bold" v-text="task.name" />
          </div>
        </LinkOrAnchor>
        <router-link
          v-else-if="task.favoriteList"
          :to="{ name: 'favorite_list', params: { listId: task.favoriteList.id } }"
          :data-pendo="PendoFeature.Favorites.OpenList"
          class="hover:bg-seagreen-pop flex flex-col px-5 py-3 transition focus-visible:ring"
        >
          <div class="relative">
            <SharedCardBadge v-if="task?.homework" type="banner" />
            <span class="text-sm" v-text="t('share.list')" />
            <p class="font-bold">
              {{ task.favoriteList?.name || task.name }}
            </p>
          </div>
        </router-link>
        <LinkOrAnchor
          v-else-if="task.content"
          :link="buildResourceUrl(task.content)"
          :data-pendo="PendoFeature.Share.Open"
          class="hover:bg-seagreen-pop flex flex-col px-5 py-3 transition focus-visible:ring"
        >
          <div class="relative">
            <SharedCardBadge v-if="task?.homework" type="banner" />
            <span class="text-sm" v-text="getDescriptor(task.content)" />
            <p class="font-bold" v-text="task.content.title" />
          </div>
        </LinkOrAnchor>
      </li>
    </ul>
    <router-link to="/fra-laerer" class="hover:bg-seagreen-pop mt-auto flex items-center gap-3 rounded-b-xl p-3 font-medium focus-visible:ring">
      <font-awesome-icon :icon="faArrowRightLong" size="lg" />
      {{ t('share.viewAll') }}
    </router-link>
  </div>
</template>
